import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAppDispatch } from '@/features/hooks';
import { previewTransaction } from '@/features/subscriptions/subscriptionsSlice';
import { useCurrency } from '@/hooks/useCurrency';
import { Loader2 } from 'lucide-react';

interface TransactionPreview {
  description?: string;
  amount?: number;
  type?: 'expense' | 'income';
  date?: string;
  category?: string;
  tags?: string[];
  merchant?: string;
  notes?: string;
  emoji?: string;
  needsReview?: boolean;
}

interface TransactionPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionId: string | null;
  onConfirm: () => void;
}

export default function TransactionPreviewDialog({
  open,
  onOpenChange,
  subscriptionId,
  onConfirm,
}: TransactionPreviewDialogProps) {
  const dispatch = useAppDispatch();
  const { fmt } = useCurrency();
  const [preview, setPreview] = useState<TransactionPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (open && subscriptionId) {
      setIsLoading(true);
      setError(null);
      setPreview(null);
      dispatch(previewTransaction(subscriptionId))
        .unwrap()
        .then((data) => setPreview(data as TransactionPreview))
        .catch((err) => setError(typeof err === 'string' ? err : 'Failed to load preview'))
        .finally(() => setIsLoading(false));
    }
  }, [open, subscriptionId, dispatch]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      onConfirm();
    } finally {
      setIsConfirming(false);
    }
  };

  const PreviewRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex justify-between items-start py-1.5 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-right font-medium break-words ml-4">{value || '—'}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="preview-dialog">
        <DialogHeader>
          <DialogTitle>Transaction Preview</DialogTitle>
          <DialogDescription>
            Review the transaction that will be generated before confirming.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="space-y-3 py-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        )}

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {preview && !isLoading && (
          <div className="divide-y">
            {preview.description && (
              <PreviewRow label="Description" value={preview.description} />
            )}
            {preview.amount != null && (
              <PreviewRow
                label="Amount"
                value={
                  <span className={preview.type === 'income' ? 'text-green-600' : 'text-red-600'}>
                    {preview.type === 'income' ? '+' : '-'}{fmt(Math.abs(preview.amount))}
                  </span>
                }
              />
            )}
            {preview.type && (
              <PreviewRow
                label="Type"
                value={<Badge variant="secondary" className="capitalize">{preview.type}</Badge>}
              />
            )}
            {preview.date && (
              <PreviewRow
                label="Date"
                value={new Date(preview.date).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              />
            )}
            {preview.category && (
              <PreviewRow label="Category" value={preview.category} />
            )}
            {preview.merchant && (
              <PreviewRow label="Merchant" value={preview.merchant} />
            )}
            {preview.tags && preview.tags.length > 0 && (
              <PreviewRow
                label="Tags"
                value={
                  <div className="flex flex-wrap gap-1 justify-end">
                    {preview.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                }
              />
            )}
            {preview.notes && (
              <PreviewRow label="Notes" value={preview.notes} />
            )}
            {preview.needsReview && (
              <PreviewRow
                label="Review"
                value={<Badge variant="secondary">Needs Review</Badge>}
              />
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !!error || isConfirming}
            data-testid="preview-confirm-btn"
          >
            {isConfirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
